import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Location } from '@angular/common';
import { zip } from 'rxjs/operators';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { InstallationEquipmentInfoPluginService } from './equipment-info.service';
import { ModalComponent } from '../../../modal/modal.component';
import { MachineFamillyService } from '../../../shared/services';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import { BaseInstallationEditPageComponent } from '../base-installation/edit-view.component';

@Component({
    selector: 'installation-equipment-info-edit',
    templateUrl: './equipment-info-edit.component.html',
    standalone: false
})
export class InstallationEquipmentInfoEditPageComponent
  extends BaseInstallationEditPageComponent
  implements OnInit, OnDestroy
{
  plugin: any;
  machine: any;
  productLines: any[];
  categories: any[];
  currentItem: any;
  productLineSelectorValues: any = [];
  editForm: UntypedFormGroup;

  private sub = new Subscription();

  machineModels: any;
  @ViewChild('modelsModal', { static: true })
  modal: ModalComponent;
  @ViewChild('productLinesModal', { static: true })
  productLinesModal: ModalComponent;
  @ViewChild('supplierModal', { static: true })
  supplierModal: ModalComponent;

  constructor(
    protected activatedRoute: ActivatedRoute,
    protected machineFamillyService: MachineFamillyService,
    protected router: Router,
    protected pluginService: InstallationEquipmentInfoPluginService,
    location: Location,
    protected cdRef: ChangeDetectorRef
  ) {
    super(location, pluginService, activatedRoute, router);
  }

  get formData() {
    return <UntypedFormArray>this.editForm.controls['data'];
  }

  ngOnInit(): void {
    this.sub.add(
      this.initRoute().subscribe(() => {
        this.initData();
      })
    );
  }

  public openModelsSelector(i: number): void {
    const control = <UntypedFormArray>this.editForm.controls['data'];
    this.currentItem = control.controls[i];
    const modelsValues = this.updateModelSelectorValues(this.currentItem.controls['ProductLine'].value, this.currentItem.controls['name'].value);
    this.openSelector(this.modal, modelsValues, this.currentItem.controls['name'].value, { search: true });
  }

  public updateModelSelectorValues(id: any, selectedName?: string) {
    if (id) {
      let categories = _.filter(this.categories, ['productLineId', id]);
      categories = _.map(categories, 'id');
      const machinesComboData = _.filter(
        this.machineModels,
        (m: any) =>
          (!m.Hidden || m.Name === selectedName) &&
          _.find(categories, (c: any) => {
            return c === m.CategoryId;
          }) !== undefined
      );
      const models = _.map(machinesComboData, 'Name').sort();
      return models.map((m: any) => ({ label: m, value: m }));
    }
    return [];
  }

  public openProductLinesSelector(i: number): void {
    const control = <UntypedFormArray>this.editForm.controls['data'];
    this.currentItem = control.controls[i];
    const productIndex = this.productLines.findIndex((pl) => pl.id === this.currentItem.controls['ProductLine'].value);
    this.openSelector(this.productLinesModal, this.productLineSelectorValues, productIndex);
  }

  public openSupplierSelector(i: number): void {
    const control = <UntypedFormArray>this.editForm.controls['data'];
    this.currentItem = control.controls[i];
    this.openSelector(
      this.supplierModal,
      this.pluginService.getSuppliers(),
      this.currentItem.controls['Supplier'].value
    );
  }

  public selectModelsMenu(item: any) {
    // selector modal was opened with {label, value}[], so item.value is the selected object
    // and item.selectedIndex is the value field of the selected object
    this.currentItem.controls['name'].setValue(item.selectedIndex);
    this.currentItem.controls['name'].markAsTouched();
  }

  public selectProductLinesMenu(item: any) {
    if (this.currentItem.controls['ProductLine'].value !== this.productLines[item.selectedIndex].id) {
      this.currentItem.controls['name'].setValue('');
    }
    this.currentItem.controls['ProductLine'].setValue(this.productLines[item.selectedIndex].id);
    this.currentItem.controls['ProductLine'].markAsTouched();
  }

  public selectSupplierMenu(item: any) {
    const supplierControl: UntypedFormControl = this.currentItem.controls['Supplier'];
    const productLineControl: UntypedFormControl = this.currentItem.controls['ProductLine'];
    const nameControl: UntypedFormControl = this.currentItem.controls['name'];
    const serialNumberControl: UntypedFormControl = this.currentItem.controls['SerialNumber'];
    supplierControl.setValue(item.selectedIndex);
    this.currentItem.controls['Supplier'].markAsTouched();
    if (supplierControl.value === 1) {
      productLineControl.addValidators(Validators.required);
      nameControl.addValidators(Validators.required);
      serialNumberControl.removeValidators(Validators.required);
    } else if (supplierControl.value === 2) {
      serialNumberControl.addValidators(Validators.required);
      productLineControl.removeValidators(Validators.required);
      nameControl.removeValidators(Validators.required);
    }
    serialNumberControl.updateValueAndValidity();
    productLineControl.updateValueAndValidity();
    nameControl.updateValueAndValidity();
  }

  public onProductLinesMenuClose(e: any) {
    this.currentItem.controls['ProductLine'].markAsTouched();
  }

  public onClose() {
    this.currentItem.controls['name'].markAsTouched();
  }

  public onSupplierMenuClose() {
    this.currentItem.controls['Supplier'].markAsTouched();
  }

  public add() {
    const control = <UntypedFormArray>this.editForm.controls['data'];
    control.push(this.initNames());
  }

  public remove(i: number) {
    const control = <UntypedFormArray>this.editForm.controls['data'];
    control.removeAt(i);
  }

  public save(model: any) {
    this.processing = true;
    this.pluginService.updateMachines(this.inspectionId, this.pluginId, this.getModel(model.data)).subscribe((el) => {
      this.processing = false;
      this.goToBack();
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  private getModel(data) {
    data.forEach((d) => {
      const selectedItem = _.find(this.machineModels, (o: any) => o.Name === d.name);
      if (selectedItem) {
        d.ModelId = selectedItem.Id;
      }
    });

    return data;
  }

  private initData() {
    this.service.getPlugin(this.inspectionId, this.pluginId).subscribe((el) => {
      this.plugin = el;
      this.machine =
        el.inspection.data != null && el.inspection.data.machine != null ? el.inspection.data.machine : null;
      if (this.machine == null || (this.machine && this.machine.length === 0)) {
        this.initMachine();
      }
      if (this.machine && !(this.machine instanceof Array)) {
        this.machine = [this.machine];
      }

      this.loadData();

      this.initForm();
      this.cdRef.detectChanges();
    });
  }

  private initMachine() {
    this.machine = [
      {
        name: '',
        ProductLine: '',
        SerialNumber: '',
        Supplier: ''
      }
    ];
  }

  private loadData() {
    this.machineFamillyService
      .getCurrentScopeProductLines()
      .pipe(zip(this.machineFamillyService.getCategories(), this.pluginService.getModels()))
      .subscribe(([lines, categories, models]) => {
        this.productLines = lines;
        this.categories = categories;
        this.machineModels = models;
        this.productLineSelectorValues = this.productLines.map((p) => p.name);
      });
  }

  private initForm() {
    this.editForm = new UntypedFormGroup({
      data: new UntypedFormArray([])
    });

    const control = <UntypedFormArray>this.editForm.controls['data'];
    this.machine.forEach((m) => {
      control.push(this.initNames(m));
    });
  }

  private initNames(el?) {
    return new UntypedFormGroup({
      name: new UntypedFormControl(el?.name ?? '', el?.Supplier === 1 ? Validators.required : []),
      ProductLine: new UntypedFormControl(el?.ProductLine ?? '', el?.Supplier === 1 ? Validators.required : []),
      SerialNumber: new UntypedFormControl(el?.SerialNumber ?? '', el?.Supplier === 2 ? Validators.required : []),
      Supplier: new UntypedFormControl(el?.Supplier ?? '', Validators.required)
    });
  }
}
